import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import type { Mode, ZoneOutput, Cuboid, OccupancyBox } from '../calculator';
import { EVM_BOARD_IMAGE } from '../assets/evmBoardImage';

const SEAT_COLORS = [
    0x1e88e5,  /* Driver       — blue   */
    0x8e24aa,  /* Passenger    — purple */
    0x43a047,  /* 2nd row drv  — green  */
    0xfb8c00,  /* Middle       — orange */
    0xe53935,  /* 2nd row pass — red    */
];

const SEAT_LABELS = [
    'Seat 0: Driver',
    'Seat 1: Passenger',
    'Seat 2: 2nd Row Driver',
    'Seat 3: Middle',
    'Seat 4: 2nd Row Passenger',
];

const ZONE_LABELS = ['Body', 'Leg', 'Footwell'];

const HIGHLIGHT_COLOR = 0xffffff;
const OCCUPANCY_BOX_COLOR = 0xe53935;

interface HoverTarget {
    hitObject: THREE.Object3D;
    label: string;
    highlight: () => void;
    unhighlight: () => void;
}

/* Config XYZ (lateral, forward/depth, up) → Three.js XYZ (lateral, up, into-screen) */
function toV3(cx: number, cy: number, cz: number): THREE.Vector3 {
    return new THREE.Vector3(cx, cz, -cy);
}

function disposeObject(root: THREE.Object3D): void {
    root.traverse(obj => {
        const geometry = (obj as THREE.Mesh).geometry;
        if (geometry)
        {
            geometry.dispose();
        }
        const material = (obj as THREE.Mesh).material;
        if (material)
        {
            if (Array.isArray(material))
            {
                material.forEach(m => m.dispose());
            }
            else
            {
                material.dispose();
            }
        }
    });
}

function buildCuboidWireframe(c: Cuboid): { group: THREE.Group; hoverTarget: HoverTarget } {
    const cx = (c.xMin + c.xMax) / 2;
    const cy = (c.yMin + c.yMax) / 2;
    const cz = (c.zMin + c.zMax) / 2;
    const w  = c.xMax - c.xMin;
    const h  = c.zMax - c.zMin;
    const d  = c.yMax - c.yMin;

    const edgeColor = SEAT_COLORS[c.seatIdx];

    const geo   = new THREE.BoxGeometry(w, h, d);
    const edges = new THREE.EdgesGeometry(geo);
    const lineMat = new THREE.LineBasicMaterial({
        color: edgeColor,
        transparent: true,
        opacity: 0.85,
    });
    const wire = new THREE.LineSegments(edges, lineMat);

    /* Invisible fill mesh used purely for raycasting + hover highlight */
    const fillMat = new THREE.MeshBasicMaterial({
        color: edgeColor,
        transparent: true,
        opacity: 0,
        depthWrite: false,
    });
    const fillMesh = new THREE.Mesh(geo, fillMat);

    const group = new THREE.Group();
    const pos = toV3(cx, cy, cz);
    group.position.set(pos.x, pos.y, pos.z);
    group.add(wire);
    group.add(fillMesh);

    const label = `${SEAT_LABELS[c.seatIdx]} — ${ZONE_LABELS[c.zoneIdx]}\n` +
        `W ${w.toFixed(2)}m  D ${d.toFixed(2)}m  H ${h.toFixed(2)}m`;

    const hoverTarget: HoverTarget = {
        hitObject: fillMesh,
        label,
        highlight: () => {
            lineMat.color.set(HIGHLIGHT_COLOR);
            lineMat.opacity = 1;
            fillMat.opacity = 0.18;
        },
        unhighlight: () => {
            lineMat.color.set(edgeColor);
            lineMat.opacity = 0.85;
            fillMat.opacity = 0;
        },
    };

    return { group, hoverTarget };
}

/* Occupancy box xMin/xMax/yMin/yMax are relative to the sensor's own
 * lateral (xOffset) and forward (yOffset) position — for example B3's
 * yMin=-0.2/yMax=0.6 means 0.2m behind and 0.6m in front of the sensor, not
 * the world origin. zMin/zMax remain ground-relative, matching the
 * cfg's occupancyBox semantics. */
function buildOccupancyBoxWireframe(
    b: OccupancyBox,
    sensorXOffset: number,
    sensorYOffset: number
): { group: THREE.Group; hoverTarget: HoverTarget } {
    const cx = sensorXOffset + (b.xMin + b.xMax) / 2;
    const cy = sensorYOffset + (b.yMin + b.yMax) / 2;
    const cz = (b.zMin + b.zMax) / 2;
    const w  = b.xMax - b.xMin;
    const h  = b.zMax - b.zMin;
    const d  = b.yMax - b.yMin;

    const edgeColor = OCCUPANCY_BOX_COLOR;

    const geo   = new THREE.BoxGeometry(w, h, d);
    const edges = new THREE.EdgesGeometry(geo);
    const lineMat = new THREE.LineBasicMaterial({
        color: edgeColor,
        transparent: true,
        opacity: 0.85,
    });
    const wire = new THREE.LineSegments(edges, lineMat);

    /* Invisible fill mesh used purely for raycasting + hover highlight */
    const fillMat = new THREE.MeshBasicMaterial({
        color: edgeColor,
        transparent: true,
        opacity: 0,
        depthWrite: false,
    });
    const fillMesh = new THREE.Mesh(geo, fillMat);

    const group = new THREE.Group();
    const pos = toV3(cx, cy, cz);
    group.position.set(pos.x, pos.y, pos.z);
    group.add(wire);
    group.add(fillMesh);

    const label = `${b.label}\n` +
        `W ${w.toFixed(2)}m  D ${d.toFixed(2)}m  H ${h.toFixed(2)}m`;

    const hoverTarget: HoverTarget = {
        hitObject: fillMesh,
        label,
        highlight: () => {
            lineMat.color.set(HIGHLIGHT_COLOR);
            lineMat.opacity = 1;
            fillMat.opacity = 0.18;
        },
        unhighlight: () => {
            lineMat.color.set(edgeColor);
            lineMat.opacity = 0.85;
            fillMat.opacity = 0;
        },
    };

    return { group, hoverTarget };
}

function makeLabel(text: string, color: number, position: THREE.Vector3): THREE.Sprite {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d')!;
    const r = (color >> 16) & 0xff;
    const g = (color >> 8) & 0xff;
    const b = color & 0xff;
    ctx.fillStyle = `rgb(${r},${g},${b})`;
    ctx.font = 'bold 42px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, 32, 32);
    const tex = new THREE.CanvasTexture(canvas);
    const mat = new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true });
    const sprite = new THREE.Sprite(mat);
    sprite.scale.set(0.15, 0.15, 1);
    sprite.position.copy(position);
    return sprite;
}

function makeAnnotLabel(text: string, pos: THREE.Vector3, color: number): THREE.Sprite {
    const W = 256, H = 56;
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = 'rgba(8,12,24,0.78)';
    ctx.fillRect(0, 0, W, H);
    const r = (color >> 16) & 0xff;
    const g = (color >> 8) & 0xff;
    const b = color & 0xff;
    ctx.fillStyle = `rgb(${r},${g},${b})`;
    ctx.font = 'bold 22px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, W / 2, H / 2);
    const tex = new THREE.CanvasTexture(canvas);
    const mat = new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true });
    const sprite = new THREE.Sprite(mat);
    sprite.scale.set(0.5, 0.11, 1);
    sprite.position.copy(pos);
    return sprite;
}

function buildDimensionAnnotations(
    yRef: number,
    y1: number,
    y2: number,
    y3: number
): { group: THREE.Group; hoverTargets: HoverTarget[] } {
    const grp = new THREE.Group();
    const hoverTargets: HoverTarget[] = [];

    const sensorY    = yRef - y1;
    const row2FrontY = yRef + y3;
    const row2BackY  = yRef + y2;

    /* Ruler lives below the floor (negative config Z), centered under the
     * cabin, so it never overlaps the cabin/zone wireframes. Two stacked
     * rows keep the nested yRef/y3 pair separate from the nested y1/y2
     * pair so their brackets don't collide. */
    const annX     = 0;
    const ROW_NEAR = -0.10;
    const ROW_FAR  = -0.24;

    const C_Y1   = 0xffeb3b;
    const C_YREF = 0x81c784;
    const C_Y2   = 0x64b5f6;
    const C_Y3   = 0xff8a65;

    function addDropTick(configY: number, color: number, rowZ: number): THREE.Line {
        const pts = [
            new THREE.Vector3(annX, 0, -configY),
            new THREE.Vector3(annX, rowZ, -configY),
        ];
        const ln = new THREE.Line(
            new THREE.BufferGeometry().setFromPoints(pts),
            new THREE.LineBasicMaterial({ color })
        );
        grp.add(ln);
        return ln;
    }

    function addSpanLine(cy1: number, cy2: number, color: number, rowZ: number): THREE.Line {
        const pts = [
            new THREE.Vector3(annX, rowZ, -cy1),
            new THREE.Vector3(annX, rowZ, -cy2),
        ];
        const ln = new THREE.Line(
            new THREE.BufferGeometry().setFromPoints(pts),
            new THREE.LineBasicMaterial({ color })
        );
        grp.add(ln);
        return ln;
    }

    /* Register a hoverable region covering a measured span. On hover, the
     * span line plus both of its boundary drop-ticks brighten and a
     * tooltip reports the exact measured length. */
    function addSpanHover(
        cy1: number, cy2: number, rowZ: number,
        spanLine: THREE.Line, tickA: THREE.Line, tickB: THREE.Line,
        name: string
    ) {
        const length = Math.abs(cy2 - cy1);
        const midY   = -(cy1 + cy2) / 2;

        const hitGeo = new THREE.BoxGeometry(0.14, 0.16, Math.max(length, 0.02));
        const hitMat = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false });
        const hitMesh = new THREE.Mesh(hitGeo, hitMat);
        hitMesh.position.set(annX, rowZ, midY);
        grp.add(hitMesh);

        const lineMats   = [spanLine, tickA, tickB].map(l => l.material as THREE.LineBasicMaterial);
        const origColors = lineMats.map(m => m.color.getHex());

        hoverTargets.push({
            hitObject: hitMesh,
            label: `${name} = ${length.toFixed(3)} m`,
            highlight: () => {
                lineMats.forEach(m => m.color.set(HIGHLIGHT_COLOR));
            },
            unhighlight: () => {
                lineMats.forEach((m, i) => m.color.set(origColors[i]));
            },
        });
    }

    /* Near row: origin → yRef → row2FrontY (yRef, y3) */
    const originTickNear = addDropTick(0,         C_YREF, ROW_NEAR);
    const yRefTickNear    = addDropTick(yRef,       C_YREF, ROW_NEAR);
    const y3Tick           = addDropTick(row2FrontY, C_Y3,   ROW_NEAR);

    /* Far row: sensorY → yRef → row2BackY (y1, y2) */
    const sensorTick  = addDropTick(sensorY,   C_Y1,   ROW_FAR);
    const yRefTickFar = addDropTick(yRef,      C_YREF, ROW_FAR);
    const y2Tick       = addDropTick(row2BackY, C_Y2,   ROW_FAR);

    const yRefSpan = addSpanLine(0,      yRef,       C_YREF, ROW_NEAR);
    const y3Span   = addSpanLine(yRef,   row2FrontY, C_Y3,   ROW_NEAR);
    const y1Span   = addSpanLine(sensorY, yRef,      C_Y1,   ROW_FAR);
    const y2Span   = addSpanLine(yRef,   row2BackY,  C_Y2,   ROW_FAR);

    addSpanHover(0,       yRef,       ROW_NEAR, yRefSpan, originTickNear, yRefTickNear, 'yRef');
    addSpanHover(yRef,    row2FrontY, ROW_NEAR, y3Span,   yRefTickNear,   y3Tick,       'y3');
    addSpanHover(sensorY, yRef,       ROW_FAR,  y1Span,   sensorTick,     yRefTickFar,  'y1');
    addSpanHover(yRef,    row2BackY,  ROW_FAR,  y2Span,   yRefTickFar,    y2Tick,       'y2');

    const lblNear = ROW_NEAR - 0.06;
    const lblFar  = ROW_FAR - 0.06;
    grp.add(makeAnnotLabel(`yRef = ${yRef.toFixed(2)}m`,
        new THREE.Vector3(annX, lblNear, -yRef / 2), C_YREF));
    grp.add(makeAnnotLabel(`y3 = ${y3.toFixed(2)}m`,
        new THREE.Vector3(annX, lblNear, -(yRef + row2FrontY) / 2), C_Y3));
    grp.add(makeAnnotLabel(`y1 = ${y1.toFixed(2)}m`,
        new THREE.Vector3(annX, lblFar, -(sensorY + yRef) / 2), C_Y1));
    grp.add(makeAnnotLabel(`y2 = ${y2.toFixed(2)}m`,
        new THREE.Vector3(annX, lblFar, -(yRef + row2BackY) / 2), C_Y2));

    return { group: grp, hoverTargets };
}

/* Lateral (xOffset) and height (zOffset) dimension markers for the sensor
 * position, in the same hover-highlight ruler style as yRef/y1/y2/y3. */
function buildSensorOffsetAnnotations(
    sensorX: number,
    sensorYPos: number,
    sensorZ: number
): { group: THREE.Group; hoverTargets: HoverTarget[] } {
    const grp = new THREE.Group();
    const hoverTargets: HoverTarget[] = [];

    const C_XOFF = 0x4dd0e1;
    const C_ZOFF = 0xf06292;

    /* xOffset: lateral ruler below the floor, offset from the y-rulers'
     * rows so it never crosses their spans, sitting at the sensor's own
     * forward (Y) position and spanning from center (X=0) to the sensor's
     * lateral offset. */
    const ROW_X = -0.38;

    function addTick(pts: [THREE.Vector3, THREE.Vector3], color: number): THREE.Line {
        const ln = new THREE.Line(
            new THREE.BufferGeometry().setFromPoints(pts),
            new THREE.LineBasicMaterial({ color })
        );
        grp.add(ln);
        return ln;
    }

    const xTickOrigin = addTick(
        [new THREE.Vector3(0, 0, -sensorYPos), new THREE.Vector3(0, ROW_X, -sensorYPos)], C_XOFF
    );
    const xTickSensor = addTick(
        [new THREE.Vector3(sensorX, 0, -sensorYPos), new THREE.Vector3(sensorX, ROW_X, -sensorYPos)], C_XOFF
    );
    const xSpanLine = addTick(
        [new THREE.Vector3(0, ROW_X, -sensorYPos), new THREE.Vector3(sensorX, ROW_X, -sensorYPos)], C_XOFF
    );

    const xLength = Math.abs(sensorX);
    const xMid    = sensorX / 2;
    const xHitMesh = new THREE.Mesh(
        new THREE.BoxGeometry(Math.max(xLength, 0.02), 0.16, 0.14),
        new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false })
    );
    xHitMesh.position.set(xMid, ROW_X, -sensorYPos);
    grp.add(xHitMesh);

    const xLineMats = [xSpanLine, xTickOrigin, xTickSensor].map(l => l.material as THREE.LineBasicMaterial);
    hoverTargets.push({
        hitObject: xHitMesh,
        label: `xOffset = ${xLength.toFixed(3)} m`,
        highlight: () => xLineMats.forEach(m => m.color.set(HIGHLIGHT_COLOR)),
        unhighlight: () => xLineMats.forEach(m => m.color.set(C_XOFF)),
    });

    grp.add(makeAnnotLabel(`xOffset = ${sensorX.toFixed(2)}m`,
        new THREE.Vector3(xMid, ROW_X - 0.06, -sensorYPos), C_XOFF));

    /* zOffset: vertical ruler at the sensor's own (x, y) position, from
     * the floor up to the sensor's mounted height. */
    const zSpanLine = addTick(
        [new THREE.Vector3(sensorX, 0, -sensorYPos), new THREE.Vector3(sensorX, sensorZ, -sensorYPos)], C_ZOFF
    );

    const zLength = Math.abs(sensorZ);
    const zMid    = sensorZ / 2;
    const zHitMesh = new THREE.Mesh(
        new THREE.BoxGeometry(0.14, Math.max(zLength, 0.02), 0.14),
        new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false })
    );
    zHitMesh.position.set(sensorX, zMid, -sensorYPos);
    grp.add(zHitMesh);

    const zLineMats = [zSpanLine].map(l => l.material as THREE.LineBasicMaterial);
    hoverTargets.push({
        hitObject: zHitMesh,
        label: `zOffset = ${zLength.toFixed(3)} m`,
        highlight: () => zLineMats.forEach(m => m.color.set(HIGHLIGHT_COLOR)),
        unhighlight: () => zLineMats.forEach(m => m.color.set(C_ZOFF)),
    });

    grp.add(makeAnnotLabel(`zOffset = ${sensorZ.toFixed(2)}m`,
        new THREE.Vector3(sensorX + 0.28, zMid, -sensorYPos - 0.12), C_ZOFF));

    return { group: grp, hoverTargets };
}

let cachedEvmTexture: THREE.Texture | null = null;

/* Loaded once and reused across every sensor-marker rebuild. */
function getEvmBoardTexture(): THREE.Texture {
    if (!cachedEvmTexture)
    {
        cachedEvmTexture = new THREE.TextureLoader().load(EVM_BOARD_IMAGE);
        cachedEvmTexture.colorSpace = THREE.SRGBColorSpace;
    }
    return cachedEvmTexture;
}

const EVM_BOARD_WIDTH  = 0.15;
const EVM_BOARD_HEIGHT = 0.13;

/* PCB substrate red sampled directly from a plain (silkscreen-free) area of
 * the board photo, used to fill the back face so it reads as the same
 * board rather than a mirrored copy of the front photo. */
const EVM_BOARD_RED = 0xb8001f;

/* Length of the yellow boresight arrow. */
const TILT_ARM_LENGTH = 0.35;

/* Boresight direction (Three.js space) for a given elevation tilt (theta,
 * mirrors the sensorPosition CLI's elevTilt directly — negative = tilted
 * down) and azimuth tilt (omega, positive = clockwise about the up axis
 * when viewed from above, matching sensorPosition's azimTilt parameter).
 *
 * Elevation pivots about the board's own horizontal centerline and azimuth
 * pivots about its vertical centerline (per the physical EVM rotation
 * references). Composed as an intrinsic pan/tilt gimbal — elevation is
 * applied about the board's original horizontal axis, then azimuth swings
 * the whole result around the fixed vertical axis — elevation's axis
 * therefore stays perfectly horizontal at any azimuth heading, matching a
 * standard pan-then-tilt mount rather than a fixed-axis one. */
function boresightDirFromConfig(theta: number, omega: number): THREE.Vector3 {
    const thetaRad = -theta * Math.PI / 180;
    const omegaRad = omega * Math.PI / 180;
    return new THREE.Vector3(
        Math.cos(thetaRad) * Math.sin(omegaRad),
        -Math.sin(thetaRad),
        -Math.cos(thetaRad) * Math.cos(omegaRad)
    );
}

/* Base orientation of the EVM board at theta=0, omega=0: a 180° spin about
 * world-up, matching how the photo needs to face at rest. */
const BOARD_BASE_QUAT = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI);
const AXIS_X = new THREE.Vector3(1, 0, 0);
const AXIS_Y = new THREE.Vector3(0, 1, 0);

/* Orientation for the EVM board matching boresightDirFromConfig exactly,
 * built by composing elementary axis-angle rotations rather than
 * reconstructing a (right, up, forward) basis from the boresight vector
 * via cross products. A cross-product basis is only ever singular at the
 * exact pole (forward parallel to world-up), but its *sign* flips as the
 * boresight sweeps past that pole — which happens continuously as theta
 * crosses ±90°, not just exactly at it — causing the board image to flip
 * 180° there. Composing rotations directly has no such discontinuity: a
 * rotation by any angle about a fixed axis is always smooth. */
function boardQuaternionFromTilt(theta: number, omega: number): THREE.Quaternion {
    const elevQuat = new THREE.Quaternion().setFromAxisAngle(AXIS_X, theta * Math.PI / 180);
    const azimQuat = new THREE.Quaternion().setFromAxisAngle(AXIS_Y, -omega * Math.PI / 180);
    return azimQuat.multiply(elevQuat).multiply(BOARD_BASE_QUAT);
}

function buildSensorMarker(
    sp: { xOffset: number; yOffset: number; zOffset: number },
    theta: number,
    omega: number
): {
    group: THREE.Group;
    boardMesh: THREE.Mesh;
    boardGroup: THREE.Group;
} {
    const group = new THREE.Group();
    const pos = toV3(sp.xOffset, sp.yOffset, sp.zOffset);
    group.position.copy(pos);

    const boresightDir = boresightDirFromConfig(theta, omega);

    /* AWRL6888EVM board image at the sensor origin, its face rotated so its
     * normal points straight out along the boresight direction. */
    const boardGeo  = new THREE.PlaneGeometry(EVM_BOARD_WIDTH, EVM_BOARD_HEIGHT);
    const boardQuat = boardQuaternionFromTilt(theta, omega);

    const boardGroup = new THREE.Group();
    boardGroup.quaternion.copy(boardQuat);

    /* Front face: the board photo. Back face: solid PCB red, so it never
     * shows a mirrored copy of the front photo from the far side. */
    const frontMesh = new THREE.Mesh(
        boardGeo,
        new THREE.MeshBasicMaterial({ map: getEvmBoardTexture(), side: THREE.FrontSide })
    );
    const backMesh = new THREE.Mesh(
        boardGeo,
        new THREE.MeshBasicMaterial({ color: EVM_BOARD_RED, side: THREE.BackSide })
    );
    boardGroup.add(frontMesh, backMesh);

    /* Invisible double-sided hit target used purely for raycasting, so
     * hover/drag keep working regardless of which face the camera sees. */
    const boardMesh = new THREE.Mesh(
        boardGeo,
        new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, side: THREE.DoubleSide, depthWrite: false })
    );
    boardGroup.add(boardMesh);

    group.add(boardGroup);

    group.add(new THREE.ArrowHelper(
        boresightDir, new THREE.Vector3(0, 0, 0), TILT_ARM_LENGTH, 0xffeb3b, 0.10, 0.06
    ));

    /* Axis arrows at sensor position */
    const axisLen = 0.25;
    const headLen = 0.07;
    const headW   = 0.04;

    /* Config X (driver side +, passenger –) → Three.js +X, red */
    group.add(new THREE.ArrowHelper(
        new THREE.Vector3(1, 0, 0), new THREE.Vector3(0, 0, 0), axisLen, 0xff4444, headLen, headW
    ));
    /* Config Y (rear +, front window = 0) → Three.js -Z, green */
    group.add(new THREE.ArrowHelper(
        new THREE.Vector3(0, 0, -1), new THREE.Vector3(0, 0, 0), axisLen, 0x44dd44, headLen, headW
    ));
    /* Config Z (up) → Three.js +Y, blue */
    group.add(new THREE.ArrowHelper(
        new THREE.Vector3(0, 1, 0), new THREE.Vector3(0, 0, 0), axisLen, 0x4499ff, headLen, headW
    ));

    /* Labels */
    group.add(makeLabel('X', 0xff4444, new THREE.Vector3(axisLen + 0.09,  0,             0)));
    group.add(makeLabel('Y', 0x44dd44, new THREE.Vector3(0,               0,            -(axisLen + 0.09))));
    group.add(makeLabel('Z', 0x4499ff, new THREE.Vector3(0,               axisLen + 0.09, 0)));

    return { group, boardMesh, boardGroup };
}

interface Props {
    output: ZoneOutput;
    mode: Mode;
    ceilingHeight: number;
    yRef: number;
    y1: number;
    y2: number;
    y3: number;
    theta: number;
    omega: number;
    /* Called continuously while the sensor board is being dragged, with
     * the new lateral offset (xOffset) and sensor-to-front offset (y1)
     * implied by the drag position (yRef and height are held fixed). */
    onSensorDrag?: (xOffset: number, y1: number) => void;
}

export function ZoneVisualizer({
    output, mode, ceilingHeight, yRef, y1, y2, y3, theta, omega, onSensorDrag,
}: Props) {
    const mountRef     = useRef<HTMLDivElement>(null);
    const tooltipRef    = useRef<HTMLDivElement>(null);
    const rendererRef   = useRef<THREE.WebGLRenderer | null>(null);
    const cameraRef     = useRef<THREE.PerspectiveCamera | null>(null);
    const controlsRef   = useRef<OrbitControls | null>(null);
    const sceneRef      = useRef<THREE.Scene | null>(null);
    const dynGroupRef   = useRef<THREE.Group | null>(null);
    const animFrameRef  = useRef<number>(0);
    const hoverTargetsRef = useRef<HoverTarget[]>([]);
    const hoveredRef      = useRef<HoverTarget | null>(null);
    const raycasterRef    = useRef<THREE.Raycaster>(new THREE.Raycaster());
    const boardMeshRef    = useRef<THREE.Mesh | null>(null);
    const draggingRef     = useRef(false);
    const dragPlaneRef    = useRef(new THREE.Plane());
    const yRefRef         = useRef(yRef);
    const onSensorDragRef = useRef(onSensorDrag);

    useEffect(() => {
        onSensorDragRef.current = onSensorDrag;
    }, [onSensorDrag]);

    useEffect(() => {
        yRefRef.current = yRef;
    }, [yRef]);

    /* One-time scene init */
    useEffect(() => {
        const el = mountRef.current;
        if (!el) return;

        const w = el.clientWidth  || 600;
        const h = el.clientHeight || 400;

        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0x111827);
        sceneRef.current = scene;

        /* Ambient + directional light */
        scene.add(new THREE.AmbientLight(0xffffff, 0.6));
        const dir = new THREE.DirectionalLight(0xffffff, 0.8);
        dir.position.set(1, 2, 2);
        scene.add(dir);

        const camera = new THREE.PerspectiveCamera(50, w / h, 0.01, 20);
        /* Position: above and behind the sensor, looking into cabin */
        camera.position.set(0, 1.8, 2.0);
        camera.lookAt(0, 0.5, 0);
        cameraRef.current = camera;

        const renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setPixelRatio(window.devicePixelRatio);
        renderer.setSize(w, h);
        el.appendChild(renderer.domElement);
        rendererRef.current = renderer;

        const controls = new OrbitControls(camera, renderer.domElement);
        controls.target.set(0, 0.5, 0);
        controls.enableDamping = true;
        controls.dampingFactor = 0.08;
        controls.screenSpacePanning = true;
        controls.mouseButtons = {
            LEFT:   THREE.MOUSE.ROTATE,
            MIDDLE: THREE.MOUSE.PAN,
            RIGHT:  THREE.MOUSE.DOLLY,
        };
        controls.update();
        controlsRef.current = controls;

        /* Ctrl + left-click pans instead of rotating; released back to
         * rotate on keyup so the default drag behaviour is unaffected. */
        function onKeyDown(event: KeyboardEvent) {
            if (event.key === 'Control') controls.mouseButtons.LEFT = THREE.MOUSE.PAN;
        }
        function onKeyUp(event: KeyboardEvent) {
            if (event.key === 'Control') controls.mouseButtons.LEFT = THREE.MOUSE.ROTATE;
        }
        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('keyup', onKeyUp);

        /* Dynamic objects group */
        const dynGroup = new THREE.Group();
        scene.add(dynGroup);
        dynGroupRef.current = dynGroup;

        /* Hover raycasting */
        function updateHover(clientX: number, clientY: number) {
            const rect = renderer.domElement.getBoundingClientRect();
            const nx = ((clientX - rect.left) / rect.width) * 2 - 1;
            const ny = -((clientY - rect.top) / rect.height) * 2 + 1;

            raycasterRef.current.setFromCamera(new THREE.Vector2(nx, ny), camera);
            const targets = hoverTargetsRef.current;
            const hits = raycasterRef.current.intersectObjects(targets.map(t => t.hitObject), false);

            const tip = tooltipRef.current;

            if (hits.length > 0)
            {
                /* The sensor board sits inside a seat cuboid's bounding box,
                 * so both can be hit by the same ray. Prioritize the board
                 * regardless of which is nearer, so it stays draggable
                 * instead of the cuboid it happens to be sitting inside. */
                const board = boardMeshRef.current;
                const boardHit = board ? hits.find(h => h.object === board) : undefined;
                const hitObj = boardHit ? boardHit.object : hits[0].object;
                const target = targets.find(t => t.hitObject === hitObj) ?? null;

                if (target !== hoveredRef.current)
                {
                    hoveredRef.current?.unhighlight();
                    target?.highlight();
                    hoveredRef.current = target;
                }

                if (tip && target)
                {
                    tip.style.display = 'block';
                    tip.style.left = `${clientX - rect.left + 14}px`;
                    tip.style.top  = `${clientY - rect.top + 14}px`;
                    tip.textContent = target.label;
                }

                renderer.domElement.style.cursor =
                    hitObj === boardMeshRef.current ? 'grab' : 'pointer';
            }
            else if (hoveredRef.current)
            {
                hoveredRef.current.unhighlight();
                hoveredRef.current = null;

                if (tip)
                {
                    tip.style.display = 'none';
                }

                renderer.domElement.style.cursor = 'auto';
            }
        }

        /* Drag the sensor board across the floor-parallel plane at its
         * current height. Lateral movement maps to sensorXOffset; forward/
         * back movement maps to y1 (sensor offset from yRef), keeping yRef
         * itself fixed. */
        function dragPoint(clientX: number, clientY: number): THREE.Vector3 | null {
            const rect = renderer.domElement.getBoundingClientRect();
            const nx = ((clientX - rect.left) / rect.width) * 2 - 1;
            const ny = -((clientY - rect.top) / rect.height) * 2 + 1;

            raycasterRef.current.setFromCamera(new THREE.Vector2(nx, ny), camera);
            const hit = new THREE.Vector3();
            const ok = raycasterRef.current.ray.intersectPlane(dragPlaneRef.current, hit);
            return ok ? hit : null;
        }

        function onPointerMove(event: PointerEvent) {
            if (draggingRef.current)
            {
                const hit = dragPoint(event.clientX, event.clientY);
                if (hit)
                {
                    const configX = hit.x;
                    const configY = -hit.z;
                    onSensorDragRef.current?.(configX, yRefRef.current - configY);
                }
                return;
            }
            updateHover(event.clientX, event.clientY);
        }

        function onPointerLeave() {
            if (draggingRef.current) return;

            hoveredRef.current?.unhighlight();
            hoveredRef.current = null;

            const tip = tooltipRef.current;
            if (tip)
            {
                tip.style.display = 'none';
            }
        }

        function onPointerDown(event: PointerEvent) {
            if (event.button !== 0) return;

            const board = boardMeshRef.current;
            if (!board || hoveredRef.current?.hitObject !== board) return;

            const worldPos = new THREE.Vector3();
            board.getWorldPosition(worldPos);
            dragPlaneRef.current.setFromNormalAndCoplanarPoint(
                new THREE.Vector3(0, 1, 0), worldPos
            );

            draggingRef.current = true;
            controls.enabled = false;
            renderer.domElement.style.cursor = 'grabbing';
            renderer.domElement.setPointerCapture(event.pointerId);
        }

        function onPointerUp(event: PointerEvent) {
            if (!draggingRef.current) return;

            draggingRef.current = false;
            controls.enabled = true;
            renderer.domElement.style.cursor = 'auto';
            if (renderer.domElement.hasPointerCapture(event.pointerId))
            {
                renderer.domElement.releasePointerCapture(event.pointerId);
            }
        }

        renderer.domElement.addEventListener('pointermove', onPointerMove);
        renderer.domElement.addEventListener('pointerleave', onPointerLeave);
        renderer.domElement.addEventListener('pointerdown', onPointerDown);
        renderer.domElement.addEventListener('pointerup', onPointerUp);

        /* Render loop */
        function animate() {
            animFrameRef.current = requestAnimationFrame(animate);
            controls.update();
            renderer.render(scene, camera);
        }
        animate();

        /* ResizeObserver */
        const ro = new ResizeObserver(() => {
            const nw = el.clientWidth;
            const nh = el.clientHeight;
            if (nw > 0 && nh > 0)
            {
                camera.aspect = nw / nh;
                camera.updateProjectionMatrix();
                renderer.setSize(nw, nh);
            }
        });
        ro.observe(el);

        return () => {
            cancelAnimationFrame(animFrameRef.current);
            ro.disconnect();
            renderer.domElement.removeEventListener('pointermove', onPointerMove);
            renderer.domElement.removeEventListener('pointerleave', onPointerLeave);
            renderer.domElement.removeEventListener('pointerdown', onPointerDown);
            renderer.domElement.removeEventListener('pointerup', onPointerUp);
            window.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('keyup', onKeyUp);
            controls.dispose();
            renderer.dispose();
            if (el.contains(renderer.domElement))
            {
                el.removeChild(renderer.domElement);
            }
        };
    }, []);

    /* Rebuild dynamic objects when output or cabin dims change */
    useEffect(() => {
        const grp = dynGroupRef.current;
        if (!grp) return;

        /* Clear previous hover state + geometry/material resources */
        hoveredRef.current?.unhighlight();
        hoveredRef.current = null;
        if (tooltipRef.current)
        {
            tooltipRef.current.style.display = 'none';
        }

        while (grp.children.length > 0)
        {
            const child = grp.children[0];
            grp.remove(child);
            disposeObject(child);
        }

        const newHoverTargets: HoverTarget[] = [];

        /* Cuboids */
        for (const c of output.cuboids)
        {
            const { group: cuboidGroup, hoverTarget } = buildCuboidWireframe(c);
            grp.add(cuboidGroup);
            newHoverTargets.push(hoverTarget);
        }

        /* Occupancy boxes (Intruder Detection mode) — positioned relative
         * to the sensor's own lateral/forward offset, so they move with
         * the sensor marker when it is dragged. */
        for (const b of output.occupancyBoxes)
        {
            const { group: boxGroup, hoverTarget } = buildOccupancyBoxWireframe(
                b, output.sensorPosition.xOffset, output.sensorPosition.yOffset
            );
            grp.add(boxGroup);
            newHoverTargets.push(hoverTarget);
        }

        /* Sensor marker (draggable AWRL6888EVM board) */
        const { group: sensorGroup, boardMesh, boardGroup } =
            buildSensorMarker(output.sensorPosition, theta, omega);
        grp.add(sensorGroup);
        boardMeshRef.current = boardMesh;

        const boardBaseScale = boardGroup.scale.clone();
        newHoverTargets.push({
            hitObject: boardMesh,
            label: 'AWRL6888EVM sensor board\n(drag to reposition)',
            highlight: () => {
                boardGroup.scale.copy(boardBaseScale).multiplyScalar(1.08);
            },
            unhighlight: () => {
                boardGroup.scale.copy(boardBaseScale);
            },
        });

        /* yRef/y1/y2/y3/xOffset/zOffset annotations only apply to SBR and
         * CPD/LPD modes; Intruder Detection has no corresponding inputs. */
        if (mode !== 'IntruderDetection') {
            /* Dimension annotations (yRef, y1, y2, y3) */
            const { group: dimGroup, hoverTargets: dimHoverTargets } =
                buildDimensionAnnotations(yRef, y1, y2, y3);
            grp.add(dimGroup);
            newHoverTargets.push(...dimHoverTargets);

            /* Dimension annotations (sensor xOffset, zOffset) */
            const { group: sensorDimGroup, hoverTargets: sensorDimHoverTargets } =
                buildSensorOffsetAnnotations(
                    output.sensorPosition.xOffset, output.sensorPosition.yOffset, output.sensorPosition.zOffset
                );
            grp.add(sensorDimGroup);
            newHoverTargets.push(...sensorDimHoverTargets);
        }

        hoverTargetsRef.current = newHoverTargets;

    }, [output, mode, ceilingHeight, yRef, y1, y2, y3, theta, omega]);

    const handleResetView = () => {
        const camera   = cameraRef.current;
        const controls = controlsRef.current;
        if (!camera || !controls) return;

        camera.position.set(0, 1.8, 2.0);
        controls.target.set(0, 0.5, 0);
        controls.update();
    };

    return (
        <div className="viz-wrapper">
            <div ref={mountRef} className="viz-canvas" />
            <div ref={tooltipRef} className="viz-tooltip" style={{ display: 'none' }} />
            <div className="viz-legend">
                {output.occupancyBoxes.length > 0 ? (
                    <div className="viz-legend-item">
                        <span
                            className="viz-legend-swatch"
                            style={{ background: '#' + OCCUPANCY_BOX_COLOR.toString(16).padStart(6, '0') }}
                        />
                        <span>Occupancy box</span>
                    </div>
                ) : SEAT_LABELS.map((label, i) => (
                    <div key={i} className="viz-legend-item">
                        <span
                            className="viz-legend-swatch"
                            style={{ background: '#' + SEAT_COLORS[i].toString(16).padStart(6, '0') }}
                        />
                        <span>{label}</span>
                    </div>
                ))}
                <div className="viz-legend-item" style={{ marginTop: 4 }}>
                    <span className="viz-legend-swatch" style={{ background: '#ffeb3b' }} />
                    <span>Sensor board (drag to move)</span>
                </div>
                <div className="viz-legend-item">
                    <span className="viz-legend-swatch" style={{ background: '#ffeb3b' }} />
                    <span>Boresight direction</span>
                </div>
            </div>
            <button className="viz-reset-btn" onClick={handleResetView}>Reset View</button>
        </div>
    );
}
